import type { ReactElement } from "react";

/**
 * Line icons, drawn inline.
 *
 * Inline SVG rather than an icon font or a package: it keeps the strict CSP
 * (no external fonts to allow), adds nothing to the dependency tree, and
 * inherits `currentColor` so icons follow the room's theme without a second
 * set of colour rules.
 *
 * All paths share a 24-unit box, 1.75 stroke, round caps and joins, so they
 * sit together evenly at any size.
 */

export type IconName =
  | "mic"
  | "micOff"
  | "camera"
  | "cameraOff"
  | "screen"
  | "note"
  | "image"
  | "brush"
  | "backdrop"
  | "trash"
  | "bringForward"
  | "sendBack"
  | "square"
  | "rounded"
  | "circle"
  | "panel"
  | "leave"
  | "send"
  | "link"
  | "smile"
  | "plus"
  | "check"
  | "close"
  | "expand"
  | "collapse"
  | "caret";

const PATHS: Record<IconName, ReactElement> = {
  mic: (
    <>
      <rect x="9" y="2.5" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0M12 18v3.5M8.5 21.5h7" />
    </>
  ),
  micOff: (
    <>
      <path d="M15 5a3 3 0 0 0-6 0v4m0 2.5a3 3 0 0 0 5.6 1.5" />
      <path d="M5 11a7 7 0 0 0 10.5 6M19 11a7 7 0 0 1-.4 2.3M12 18v3.5M8.5 21.5h7" />
      <path d="M3 3l18 18" />
    </>
  ),
  camera: (
    <>
      <rect x="2.5" y="6" width="13" height="12" rx="2.5" />
      <path d="M15.5 10.5l6-3v9l-6-3z" />
    </>
  ),
  cameraOff: (
    <>
      <path d="M15.5 10.5l6-3v9l-4-2" />
      <path d="M12.5 6H5a2.5 2.5 0 0 0-2.5 2.5v7A2.5 2.5 0 0 0 5 18h8a2.5 2.5 0 0 0 2.5-2.5v-3" />
      <path d="M3 3l18 18" />
    </>
  ),
  screen: (
    <>
      <rect x="2.5" y="4" width="19" height="13" rx="2" />
      <path d="M8.5 21h7M12 17v4" />
    </>
  ),
  note: (
    <>
      <path d="M5 3.5h9.5L19 8v12.5H5z" />
      <path d="M14 3.5V8h5M8.5 12h7M8.5 16h4.5" />
    </>
  ),
  image: (
    <>
      <rect x="3" y="4.5" width="18" height="15" rx="2.5" />
      <circle cx="8.5" cy="10" r="1.75" />
      <path d="M3.5 17l5-4.5 4 3.5 3-2.5 5 4" />
    </>
  ),
  brush: (
    <>
      <path d="M14.5 3.5l6 6-8.5 8.5H6v-6z" />
      <path d="M11.5 6.5l6 6M3 21c1.5-.5 2.5-1.5 3-3" />
    </>
  ),
  backdrop: (
    <>
      <rect x="2.5" y="4.5" width="19" height="15" rx="2.5" />
      <path d="M2.5 14l5-4 4 3.5 3.5-3 6 5" />
    </>
  ),
  trash: (
    <>
      <path d="M4 6.5h16M9.5 6.5V4h5v2.5" />
      <path d="M6.5 6.5l1 13.5h9l1-13.5M10.5 10v7M13.5 10v7" />
    </>
  ),
  bringForward: (
    <>
      <rect x="3.5" y="3.5" width="11" height="11" rx="2" />
      <path d="M9.5 20.5h9a2 2 0 0 0 2-2v-9" />
    </>
  ),
  sendBack: (
    <>
      <rect x="9.5" y="9.5" width="11" height="11" rx="2" />
      <path d="M14.5 3.5h-9a2 2 0 0 0-2 2v9" />
    </>
  ),
  square: <rect x="4" y="4" width="16" height="16" />,
  rounded: <rect x="4" y="4" width="16" height="16" rx="5" />,
  circle: <circle cx="12" cy="12" r="8.5" />,
  panel: (
    <>
      <rect x="3" y="4.5" width="18" height="15" rx="2.5" />
      <path d="M14.5 4.5v15" />
    </>
  ),
  leave: (
    <>
      <path d="M14 3.5H6a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h8" />
      <path d="M17 8.5l3.5 3.5L17 15.5M9.5 12h11" />
    </>
  ),
  send: <path d="M4 12l16-8-6 8 6 8z" />,
  link: (
    <>
      <path d="M10 13.5a4 4 0 0 0 5.7.3l3-3a4 4 0 1 0-5.7-5.7l-1.7 1.7" />
      <path d="M14 10.5a4 4 0 0 0-5.7-.3l-3 3a4 4 0 1 0 5.7 5.7l1.7-1.7" />
    </>
  ),
  smile: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M8.5 14.5a4.5 4.5 0 0 0 7 0" />
      <path d="M9 9.5h.01M15 9.5h.01" />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" />,
  check: <path d="M4.5 12.5l5 5 10-11" />,
  close: <path d="M5.5 5.5l13 13M18.5 5.5l-13 13" />,
  expand: (
    <>
      <path d="M9 3.5H3.5V9M15 3.5h5.5V9M9 20.5H3.5V15M15 20.5h5.5V15" />
    </>
  ),
  collapse: (
    <>
      <path d="M3.5 9H9V3.5M20.5 9H15V3.5M3.5 15H9v5.5M20.5 15H15v5.5" />
    </>
  ),
  // The little arrow that opens a control's own menu.
  caret: <path d="M6 9.5l6 6 6-6" />,
};

export function Icon({
  name,
  size = 20,
  className,
}: {
  name: IconName;
  size?: number;
  className?: string;
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      // Decorative: every icon sits on a control that carries its own label.
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  );
}
